"use client";

import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { Button } from "@/app/(public)/shared/ui/Button";
import { StringInput } from "@/components/ui/shared/SpecialInput";
import { venuePartner } from "@/lib/database/client/database";
import { getCurrentUser } from "@/lib/db/users";
import { createClient } from "@/utils/supabase/client";
import { Check, CheckCircle } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

export default function Venues() {
  const supabase = createClient();

  // Form state
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [fullNameTouched, setFullNameTouched] = useState(false);
  const [companyNameTouched, setCompanyNameTouched] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const [workEmailTouched, setWorkEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Submit form function
  const submitForm = async () => {
    setFullNameTouched(true);
    setCompanyNameTouched(true);
    setWebsiteTouched(true);
    setWorkEmailTouched(true);
    setSubmitStatus("idle");
    setSubmitMessage("");

    if (!fullName || !companyName || !website || !workEmail || !isValidEmail(workEmail)) {
      setSubmitStatus("error");
      setSubmitMessage("Please fill in all required fields with valid information.");
      return;
    }

    setSubmitting(true);
    try {
      await venuePartner(supabase, fullName, companyName, website, workEmail, phone, description);

      // Clear the form
      setFullName("");
      setWorkEmail("");
      setCompanyName("");
      setWebsite("");
      setPhone("");
      setDescription("");
      setShowConfirmation(true);
      setFullNameTouched(false);
      setCompanyNameTouched(false);
      setWebsiteTouched(false);
      setWorkEmailTouched(false);
    } catch (err) {
      setSubmitStatus("error");
      setSubmitMessage("Submission failed. Please try again later.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-fill user data if logged in
  useEffect(() => {
    (async () => {
      const user = await getCurrentUser(supabase);
      if (user) {
        setFullName(user.first_name + " " + user.last_name);
        setWorkEmail(user.email);
      }
    })();
  }, [supabase]);

  // Update useEffect to clear error
  useEffect(() => {
    if (
      submitStatus === "error" &&
      submitMessage === "Please fill in all required fields with valid information." &&
      fullName &&
      companyName &&
      website &&
      workEmail &&
      isValidEmail(workEmail)
    ) {
      setSubmitStatus("idle");
      setSubmitMessage("");
    }
  }, [fullName, companyName, website, workEmail, submitStatus, submitMessage]);

  const handleFullNameChange = (value: string) => {
    setFullNameTouched(true);
    setFullName(value);
  };
  const handleCompanyNameChange = (value: string) => {
    setCompanyNameTouched(true);
    setCompanyName(value);
  };
  const handleWebsiteChange = (value: string) => {
    setWebsiteTouched(true);
    setWebsite(value);
  };
  const handleWorkEmailChange = (value: string) => {
    setWorkEmailTouched(true);
    setWorkEmail(value);
  };

  return (
    <main>
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px] ">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full lg:h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">Get discovered. Get booked. Get insights.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                Nowadays connects your venue with active corporate planners sourcing in your market.
              </p>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="enterprise-small"
              trustedByText="Trusted by world class companies"
              className="justify-start"
            />
          </div>
          {/* Right side - Partner Form */}
          <div className="flex items-center justify-center">
            <motion.div
              className="w-full max-w-md"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="space-y-6">
                <div className="space-y-3">
                  <h3 className="text-h4 text-landing-primary">Become a Partner</h3>
                </div>

                <div className="space-y-4">
                  {/* Your Name - Full width */}
                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Your Name*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: John Doe"
                      value={fullName}
                      setValue={handleFullNameChange}
                      validator={fullNameTouched ? fullName !== "" : true}
                    />
                    {fullNameTouched && !fullName && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Name is required.
                      </div>
                    )}
                  </div>

                  {/* Company Name and Website - Same row on desktop, stacked on mobile */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-body-sm font-medium text-landing-primary mb-2 block">Company Name*</label>
                      <StringInput
                        className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                        placeholder="Ex: ABC Corp."
                        value={companyName}
                        setValue={handleCompanyNameChange}
                        validator={companyNameTouched ? companyName !== "" : true}
                      />
                      {companyNameTouched && !companyName && (
                        <div className="text-red-500 text-sm mt-1" role="alert">
                          Company Name is required.
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-body-sm font-medium text-landing-primary mb-2 block">Website*</label>
                      <StringInput
                        className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                        placeholder="Ex: www.abccorp.com"
                        value={website}
                        setValue={handleWebsiteChange}
                        validator={websiteTouched ? website !== "" : true}
                      />
                      {websiteTouched && !website && (
                        <div className="text-red-500 text-sm mt-1" role="alert">
                          Website is required.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Work Email and Phone - Same row on desktop, stacked on mobile */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-body-sm font-medium text-landing-primary mb-2 block">Work Email*</label>
                      <StringInput
                        className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                        placeholder="Ex: john@abccorp.com"
                        value={workEmail}
                        setValue={handleWorkEmailChange}
                        validator={workEmailTouched ? workEmail !== "" && isValidEmail(workEmail) : true}
                      />
                      {workEmailTouched && (!workEmail || !isValidEmail(workEmail)) && (
                        <div className="text-red-500 text-sm mt-1" role="alert">
                          Please enter a valid email.
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-body-sm font-medium text-landing-primary mb-2 block">Phone</label>
                      <StringInput
                        className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                        placeholder="Ex: 123-456-7890"
                        value={phone}
                        setValue={setPhone}
                        validator={true}
                      />
                    </div>
                  </div>

                  {/* Brief Description - Full width */}
                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                      Brief Description of services (Use 3rd person)!
                    </label>
                    <textarea
                      className="w-full h-20 input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors resize-none"
                      placeholder=""
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  {submitStatus !== "idle" && (
                    <div
                      className={`p-3 rounded-md text-sm ${
                        submitStatus === "success"
                          ? "bg-green-50 text-green-700 border border-green-200 mb-4"
                          : "bg-red-50 text-red-700 border border-red-200 mb-4"
                      }`}
                    >
                      {submitMessage}
                    </div>
                  )}
                  <Button variant="primary" size="md" onClick={submitForm} className="w-full" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Document Style */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Become a Preferred Venue</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Join the platform where top hotels win more group business. Stand out, win more bookings, and gain access
              to unmatched market intelligence.
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Boost your revenue with actionable insights</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Get more than leads - get data. We'll show you how each client engages with your proposal, why you won
                (or didn't), and how you stack up to competitors. Track response times, status updates, and win rates
                with comprehensive analytics.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Real-time hotel intelligence, all in one place</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Make every business decision based on intelligent, real-time data. See who views your proposal, compare
                your rates to competitors, learn why proposals didn't win, and track client preferences. Monitor your
                competitive set and understand planner demand trends.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Stay ahead of the competition</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                No more guesswork. Benchmark your performance with Market Penetration Index (MPI), Average Rate Index
                (ARI), and seasonality analysis. Get unlimited daily rate shops, rate parity checks, and channel
                performance reports to position your venue effectively.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Easy onboarding with no integration required</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                It's free to join with no integrations or tech lift required. We'll onboard you in under 10 minutes and
                provide performance dashboards, client engagement insights, and data-backed tips to improve your win
                rate and profitability.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Column */}
              <div className="space-y-6 w-full lg:flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-h3 text-landing-primary">What You'll Receive</h3>
                </div>
                <div className="space-y-4 p-6 sm:p-8 rounded-2xl bg-landing-bg-primary">
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">More RFPs</div>
                      <div className="text-landing-secondary">Get qualified leads from corporate event planners</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Performance dashboards</div>
                      <div className="text-landing-secondary">Compare your metrics against competitors</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Client engagement insights</div>
                      <div className="text-landing-secondary">Get detailed feedback on every proposal</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Data-backed tips</div>
                      <div className="text-landing-secondary">Improve your win rate and profitability</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">No integration required</div>
                      <div className="text-landing-secondary">Get started with just actionable intelligence</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="w-full lg:flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-h3 text-landing-primary">Insights That Work for You</h3>
                </div>
                <div className="space-y-4 p-6 sm:p-8 rounded-2xl bg-landing-bg-primary">
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Real-time data</div>
                      <div className="text-landing-secondary">
                        Make smarter pricing and distribution decisions as demand shifts.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Like-for-like rate comparisons</div>
                      <div className="text-landing-secondary">
                        See exactly how your pricing compares to up to 10 local competitors.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Unlimited daily rate shops</div>
                      <div className="text-landing-secondary">
                        Price confidently with updated compset data, every day.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Rate parity checks</div>
                      <div className="text-landing-secondary">
                        See what your guests see across OTAs—eliminate undercutting.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-5 h-5 mt-1 text-primary flex-shrink-0" />
                    <div>
                      <div className="text-landing-primary font-semibold">Channel performance reports</div>
                      <div className="text-landing-secondary">
                        Discover which channels and geographies are delivering the most revenue.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 sm:py-0">
        <div className="">
          <Testimonial variant="single" type="general" featuredIndex={4} title="Don't just take our word for it." />
        </div>
      </section>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div
          className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={() => setShowConfirmation(false)}
        >
          <motion.div
            className="bg-white p-8 rounded-2xl max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center space-y-4">
              <div className="text-4xl">🎉</div>
              <h2 className="text-landing-primary text-h3">Thank You!</h2>
              <p className="text-body text-landing-secondary">
                Your venue partnership application has been submitted for approval. You'll receive an email within 3
                business days with any next steps!
              </p>
              <Button variant="primary" size="md" onClick={() => setShowConfirmation(false)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
}
